import { useCallback } from "react";
import { useReactFlow, useStoreApi } from "@xyflow/react";
import { CUSTOM_NODE } from "../types";
import { v4 as uuidv4 } from "uuid";
import Dagre from "@dagrejs/dagre";

const useNodesInteractions = () => {
  const {
    screenToFlowPosition,
    getNode,
    setNodes,
    addNodes,
    setEdges,
    fitView,
  } = useReactFlow();
  const store = useStoreApi();

  const creatNodeData = (type) => {
    switch (type) {
      case CUSTOM_NODE.Start:
        return {};
      case CUSTOM_NODE.Conversation:
        return {
          instruction: "",
          title: "Conversation",
          transition: [
            {
              handleId: "transition-" + uuidv4(),
              text: "Describe the transition condition",
            },
          ],
        };
      case CUSTOM_NODE.Greeting:
        return {
          instruction: "",
          title: "Greeting",
          transition: [
            {
              handleId: "transition-" + uuidv4(),
              text: "Next",
            },
          ],
        };
      case CUSTOM_NODE.HumanAgentHandoff:
        return {
          inboundHumanNumber: "",
          ringTimeoutSeconds: 10,
          greetingMessage: "",
        };
      case CUSTOM_NODE.LanguageDetection:
        return {
          detectionMethod: "both",
          languages: ["en", "es", "fr"],
          customMessage: "Which language do you prefer?",
          timeoutSeconds: 30,
        };
      default:
        return {};
    }
  };

  /**
   * 新增节点
   */
  const handleAddNode = useCallback(
    (type) => {
      const flowContainer = document.querySelector("#react-container");
      if (!flowContainer) return;

      const containerRect = flowContainer.getBoundingClientRect();

      const randomScreenX =
        containerRect.left + Math.random() * containerRect.width;
      const randomScreenY =
        containerRect.top + Math.random() * containerRect.height;

      const flowPosition = screenToFlowPosition({
        x: randomScreenX,
        y: randomScreenY,
      });

      const newNode = {
        id: uuidv4(),
        type,
        data: creatNodeData(type),
        position: flowPosition,
      };

      addNodes(newNode);
    },
    [addNodes, screenToFlowPosition]
  );

  /**
   * 选中节点
   */
  const handleSelectNode = useCallback(
    (id, selected = true, all = false) => {
      // 手动将节点设置为选中状态
      // 获取当前状态
      const { setNodes: internalSetNodes, nodes } = store.getState();

      // 直接修改节点数组
      const updatedNodes = nodes.map((node) => {
        if (all) {
          return { ...node, selected };
        } else if (node.id === id) {
          return { ...node, selected };
        }
        return { ...node, selected: false };
      });

      // 调用内部的 setNodes 来更新状态，不触发 onNodesChange
      internalSetNodes(updatedNodes);
    },
    [store]
  );

  /**
   * 复制节点
   */
  const duplicateNode = useCallback(
    (id) => {
      handleSelectNode("", false, true);
      // return
      const node = getNode(id);
      if (!node) return;
      const position = {
        x: node.position.x + 100,
        y: node.position.y + 100,
      };

      addNodes({
        ...node,
        selected: true,
        dragging: false,
        id: uuidv4(),
        position,
      });
    },
    [handleSelectNode, getNode, addNodes]
  );

  /**
   * 删除节点
   */
  const deleteNode = useCallback(
    (id) => {
      setNodes((nodes) => nodes.filter((node) => node.id !== id));
      setEdges((edges) =>
        edges.filter((edge) => edge.source !== id && edge.target !== id)
      );
    },
    [setNodes, setEdges]
  );

  /**
   * 整理节点
   */
  const handleLayout = useCallback(() => {
    const { nodes, edges } = store.getState();
    // console.log(nodes);
    const layouted = getLayoutedElements(nodes, edges);

    setNodes([...layouted.nodes]);
    setEdges([...layouted.edges]);

    fitView();
  }, [fitView, setEdges, setNodes, store]);

  const getLayoutedElements = (nodes, edges) => {
    const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: "LR", ranksep: 150, nodesep: 100 });

    edges.forEach((edge) => g.setEdge(edge.source, edge.target));
    nodes.forEach((node) =>
      g.setNode(node.id, {
        ...node,
        width: node.measured?.width ?? 0,
        height: node.measured?.height ?? 0,
      })
    );

    Dagre.layout(g);

    return {
      nodes: nodes.map((node) => {
        const position = g.node(node.id);
        // We are shifting the dagre node position (anchor=center center) to the top left
        // so it matches the React Flow node anchor point (top left).
        const x = position.x - (node.measured?.width ?? 0) / 2;
        const y = position.y - (node.measured?.height ?? 0) / 2;

        return { ...node, position: { x, y } };
      }),
      edges,
    };
  };

  return {
    handleAddNode,
    handleSelectNode,
    duplicateNode,
    deleteNode,
    handleLayout,
    getLayoutedElements
  };
};

export default useNodesInteractions;
